import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, ArrowRight, Landmark, Zap, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatUSD } from "@/lib/constants";
import { getDisplayConfig, enabledFields } from "@/lib/displayConfig";
import { useTranslation } from "react-i18next";

export default function CompareBanks({ compact = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fLabel = (f) => (t(`cmp.f.${f.key}`, { lng: "en", defaultValue: "" }) === f.label ? t(`cmp.f.${f.key}`) : f.label);
  const typeLabel = (ty) => t(`cmp.type${ty}`, { defaultValue: ty });
  const [params] = useSearchParams();
  const [banks, setBanks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fields, setFields] = useState([]);

  useEffect(() => {
    api.get("/bank-compare").then((r) => {
      const list = r.data.banks || [];
      setBanks(list);
      const fromUrl = (params.get("b") || "").split(",").map((s) => s.trim()).filter(Boolean)
        .filter((s) => list.some((b) => b.slug === s)).slice(0, 3);
      setSelected(fromUrl.length ? fromUrl : list.slice(0, 3).map((b) => b.slug));
    }).catch(() => {});
    getDisplayConfig().then((cfg) => setFields(enabledFields(cfg, "compare_bank")));
  }, []);

  const bySlug = useMemo(() => Object.fromEntries(banks.map((b) => [b.slug, b])), [banks]);
  const cols = selected.map((s) => bySlug[s]).filter(Boolean);
  const remaining = banks.filter((b) => !selected.includes(b.slug));
  const minFee = cols.length ? Math.min(...cols.map((c) => c.fee)) : 0;
  const gridCols = { gridTemplateColumns: `minmax(130px,150px) repeat(${cols.length}, minmax(150px, 1fr))` };

  const addBank = (slug) => { if (selected.length < 3 && !selected.includes(slug)) setSelected([...selected, slug]); };
  const removeBank = (slug) => setSelected(selected.filter((s) => s !== slug));
  const reset = () => setSelected(banks.slice(0, 3).map((b) => b.slug));

  if (!banks.length) return null;

  const renderVal = (c, key) => {
    if (key === "fee") return (
      <span className="flex items-center gap-2 font-mono text-base text-foreground">{formatUSD(c.fee)}
        {c.fee === minFee && cols.length > 1 && <span className="text-[10px] font-sans font-medium text-green-700 bg-green-100 rounded-full px-1.5 py-0.5">{t("cmp.lowest")}</span>}</span>);
    if (key === "type") return typeLabel(c.type);
    if (key === "currencies") return c.currencies;
    if (key === "country_count") return <span className="text-sm">{c.countries.slice(0, 3).join(", ")}{c.countries.length > 3 ? ` +${c.countries.length - 3}` : ""}</span>;
    return c[key] ? c[key] : <span className="text-muted-foreground">—</span>;
  };

  return (
    <div data-testid="compare-banks">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-sm text-muted-foreground">{t("cmp.comparing", { n: cols.length, max: 3 })}</span>
        {remaining.length > 0 && selected.length < 3 && (
          <Select value="" onValueChange={addBank}>
            <SelectTrigger className="w-[240px] rounded-sm border-slate-300 bg-card" data-testid="bank-compare-add-select">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Plus className="h-4 w-4 text-orange-600" /> {t("cmp.addBank")}</span>
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {remaining.map((b) => (<SelectItem key={b.slug} value={b.slug} data-testid={`bank-compare-option-${b.slug}`}>{b.name} · {typeLabel(b.type)} — {formatUSD(b.fee)}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        <button onClick={reset} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="bank-compare-reset"><RotateCcw className="h-3.5 w-3.5" /> {t("cmp.reset")}</button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="overflow-x-auto rounded-2xl border border-border bg-card card-soft">
        <div className="grid border-b border-border" style={gridCols}>
          <div className="px-4 py-5 bg-secondary/40" />
          {cols.map((c) => (
            <div key={c.slug} className="relative px-4 py-5 border-l border-border" data-testid={`bank-compare-col-${c.slug}`}>
              <button onClick={() => removeBank(c.slug)} disabled={cols.length <= 2} className="absolute top-3 right-3 grid place-items-center h-6 w-6 rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`bank-compare-remove-${c.slug}`} aria-label={t("cmp.remove", { name: c.name })}><X className="h-3.5 w-3.5" /></button>
              {c.logo && c.logo.startsWith("http") ? <img src={c.logo} alt={c.name} className="h-11 w-11 rounded-lg object-cover mb-3" /> : <span className="grid place-items-center h-11 w-11 rounded-lg bg-secondary text-primary mb-3">{c.logo && c.logo.length <= 4 ? <span className="text-2xl">{c.logo}</span> : <Landmark className="h-5 w-5" />}</span>}
              <p className="font-heading text-lg text-primary leading-tight">{c.name}</p>
              <span className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${c.type === "Digital" ? "text-blue-700 bg-blue-100" : "text-slate-700 bg-slate-100"}`}>{c.type === "Digital" ? <Zap className="h-3 w-3" /> : <Landmark className="h-3 w-3" />} {typeLabel(c.type)}</span>
            </div>
          ))}
        </div>

        {fields.map((f) => (
          <div key={f.key} className="grid border-b border-border" style={gridCols}>
            <div className="px-4 py-4 text-sm text-muted-foreground flex items-center gap-2 bg-secondary/40">{fLabel(f)}</div>
            {cols.map((c) => (<div key={c.slug} className="px-4 py-4 text-sm text-foreground border-l border-border" data-testid={`bank-compare-cell-${c.slug}-${f.key}`}>{renderVal(c, f.key)}</div>))}
          </div>
        ))}

        <div className="grid" style={gridCols}>
          <div className="px-4 py-5 bg-secondary/40" />
          {cols.map((c) => (
            <div key={c.slug} className="px-4 py-5 border-l border-border space-y-2">
              <Button onClick={() => navigate(`/start?pkg=bank_${c.slug}`)} className="w-full rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-9 text-sm" data-testid={`bank-compare-order-${c.slug}`}>{t("cmp.order")} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
              <Button variant="outline" onClick={() => navigate(`/bank/${c.slug}`)} className="w-full rounded-sm border-slate-300 bg-card hover:bg-secondary h-9 text-sm" data-testid={`bank-compare-details-${c.slug}`}>{t("cmp.viewDetails")}</Button>
            </div>
          ))}
        </div>
      </motion.div>

      {compact && (
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={() => navigate("/bank-compare")} className="rounded-sm border-slate-300 bg-card hover:bg-secondary" data-testid="bank-compare-see-full">{t("cmp.openFullBank")} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
        </div>
      )}
    </div>
  );
}
